import React, { useEffect, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, ChevronLeft, ChevronRight } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import { blogPosts } from '../data/blogPosts';
import Header from './Header';
import Footer from './Footer';
import FloatingIcons from './FloatingIcons';

function BlogPostDetailPage() {
  const { id } = useParams<{ id: string }>();
  const post = id ? blogPosts.find(p => p.id === id) : null;

  // Scroll to top on mount
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [id]);

  // Calculate previous and next post
  const { prevPost, nextPost } = useMemo(() => {
    if (!id) return { prevPost: null, nextPost: null };

    const currentIndex = blogPosts.findIndex(p => p.id === id);
    if (currentIndex === -1) return { prevPost: null, nextPost: null };

    // Previous = older post (higher index, later in array)
    const prevPost = currentIndex < blogPosts.length - 1 ? blogPosts[currentIndex + 1] : null;
    // Next = newer post (lower index, earlier in array)
    const nextPost = currentIndex > 0 ? blogPosts[currentIndex - 1] : null;

    return { prevPost, nextPost };
  }, [id]);

  if (!post) {
    return (
      <div className="App">
        <FloatingIcons />
        <Header />
        <div className="container" style={{ padding: '80px 24px', textAlign: 'center' }}>
          <h2>Blog Post Not Found</h2>
          <Link to="/blog" className="cta-button primary" style={{ marginTop: '24px', display: 'inline-block' }}>
            Back to Blog
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  // Format date
  const formattedDate = new Date(post.date).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });

  return (
    <div className="App">
      <FloatingIcons />
      <Header />
      <section className="career-detail-section blog-detail-section">
        <div className="container">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <Link to="/blog" className="career-back-link blog-back-link">
              <ArrowLeft size={20} />
              <span>Back to Blog</span>
            </Link>
          </motion.div>

          <motion.div
            className="blog-detail-header"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
          >
            {post.logo && (
              <div className="blog-detail-logo">
                <img src={post.logo} alt={post.title} />
              </div>
            )}
            <div className="blog-detail-title-section">
              <h1 className="blog-detail-title">{post.title}</h1>
              <div className="blog-detail-meta">
                <span className="blog-detail-date">{formattedDate}</span>
                {post.tags && post.tags.length > 0 && (
                  <div className="blog-detail-tags">
                    {post.tags.filter(tag => tag !== 'Blog Posts').map((tag) => (
                      <span key={tag} className="blog-tag">{tag}</span>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </motion.div>

          <motion.div
            className="blog-detail-content"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
          >
            <div className="blog-markdown-content">
              <ReactMarkdown>{post.content}</ReactMarkdown>
            </div>
          </motion.div>

          {/* Previous/Next Navigation */}
          {(nextPost || prevPost) && (
            <motion.div
              className="career-detail-navigation blog-detail-navigation"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
            >
              {prevPost ? (
                <Link to={`/blog/${prevPost.id}`} className="career-nav-link career-nav-prev">
                  <ChevronLeft size={20} />
                  <div className="career-nav-content">
                    <span className="career-nav-label">Previous Post</span>
                    <span className="career-nav-name">{prevPost.title}</span>
                  </div>
                </Link>
              ) : (
                <div style={{ flex: 1 }}></div>
              )}

              {nextPost ? (
                <Link to={`/blog/${nextPost.id}`} className="career-nav-link career-nav-next">
                  <div className="career-nav-content">
                    <span className="career-nav-label">Next Post</span>
                    <span className="career-nav-name">{nextPost.title}</span>
                  </div>
                  <ChevronRight size={20} />
                </Link>
              ) : (
                <div style={{ flex: 1 }}></div>
              )}
            </motion.div>
          )}
        </div>
      </section>
      <Footer />
    </div>
  );
}

export default BlogPostDetailPage;

